import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  BEARBEITUNGSFRIST_STUNDEN,
  SortierFeld,
  SortierZustand,
  Zeiteintrag
} from '../../types/zeiterfassung';
import { formatDatum, formatPause, formatRestzeit, formatStunden } from '../../utils/format';
import LeerZustand from '../common/LeerZustand';

type Props = {
  eintraege: Zeiteintrag[];
  laedt: boolean;
  gesamt: number;
  seite: number;
  proSeite: number;
  sortierung: SortierZustand;
  zeigeMitarbeiter: boolean;
  zeigeErstelltVon: boolean;
  hatFilter: boolean;
  darfBearbeiten: (eintrag: Zeiteintrag) => boolean;
  unbegrenztBearbeitbar: boolean;
  onSeiteAendern: (seite: number) => void;
  onProSeiteAendern: (proSeite: number) => void;
  onSortieren: (feld: SortierFeld) => void;
  onBearbeiten: (eintrag: Zeiteintrag) => void;
  onLoeschen: (eintrag: Zeiteintrag) => void;
};

const fristHinweis = `Nur innerhalb von ${BEARBEITUNGSFRIST_STUNDEN} Stunden (8 Tage) nach Erstellung änderbar`;

const ZeiteintragTabelle = ({
  eintraege,
  laedt,
  gesamt,
  seite,
  proSeite,
  sortierung,
  zeigeMitarbeiter,
  zeigeErstelltVon,
  hatFilter,
  darfBearbeiten,
  unbegrenztBearbeitbar,
  onSeiteAendern,
  onProSeiteAendern,
  onSortieren,
  onBearbeiten,
  onLoeschen
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const paginierung = (
    <TablePagination
      component="div"
      count={gesamt}
      page={seite}
      onPageChange={(_e, neueSeite) => onSeiteAendern(neueSeite)}
      rowsPerPage={proSeite}
      onRowsPerPageChange={e => onProSeiteAendern(parseInt(e.target.value, 10))}
      rowsPerPageOptions={[25, 50, 100, { label: 'Alle', value: -1 }]}
      labelRowsPerPage="Pro Seite:"
      labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
      getItemAriaLabel={typ =>
        ({
          first: 'Zur ersten Seite',
          last: 'Zur letzten Seite',
          next: 'Zur nächsten Seite',
          previous: 'Zur vorherigen Seite'
        }[typ])
      }
    />
  );

  const leer = !laedt && eintraege.length === 0;

  const leerAnzeige = (
    <LeerZustand
      titel={hatFilter ? 'Keine Einträge für diese Filter' : 'Noch keine Zeiteinträge'}
      hinweis={
        hatFilter
          ? 'Passen Sie die Filter an oder setzen Sie sie zurück, um mehr Einträge zu sehen.'
          : 'Erfassen Sie oben Ihre erste Arbeitszeit.'
      }
    />
  );

  const aktionen = (eintrag: Zeiteintrag) => {
    const bearbeitbar = darfBearbeiten(eintrag);
    const grund = bearbeitbar ? '' : fristHinweis;

    return (
      <>
        <Tooltip title={grund || 'Eintrag bearbeiten'}>
          <span>
            <IconButton
              size="small"
              onClick={() => onBearbeiten(eintrag)}
              disabled={!bearbeitbar}
              aria-label={`Eintrag vom ${formatDatum(eintrag.date)} bearbeiten`}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={grund || 'Eintrag löschen'}>
          <span>
            <IconButton
              size="small"
              color="error"
              onClick={() => onLoeschen(eintrag)}
              disabled={!bearbeitbar}
              aria-label={`Eintrag vom ${formatDatum(eintrag.date)} löschen`}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </>
    );
  };

  // -------------------------------------------------------------------------
  // Mobile Kartenansicht
  //
  // Vorher wurde auch auf dem Telefon die vollständige Tabelle mit über zehn
  // Spalten gerendert – ohne horizontales Scrollen war sie unbenutzbar.
  // -------------------------------------------------------------------------
  if (isMobile) {
    return (
      <Box>
        {laedt && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {[0, 1, 2].map(i => (
              <Skeleton key={i} variant="rounded" height={140} />
            ))}
          </Stack>
        )}

        {leer && <Paper>{leerAnzeige}</Paper>}

        {!laedt &&
          eintraege.map(eintrag => (
            <Card key={eintrag.id} sx={{ mb: 1.5 }}>
              <CardContent sx={{ pb: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {formatDatum(eintrag.date)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {eintrag.projectName}
                    </Typography>
                  </Box>
                  <Chip label={formatStunden(eintrag.hours)} color="primary" size="small" />
                </Box>

                <Divider sx={{ my: 1 }} />

                <Typography variant="body2">
                  {eintrag.startTime} – {eintrag.endTime}
                  {eintrag.breakDuration > 0 && ` (Pause ${eintrag.breakDuration} min)`}
                </Typography>

                {zeigeMitarbeiter && (
                  <Typography variant="body2" color="text.secondary">
                    Mitarbeiter: {eintrag.userName}
                  </Typography>
                )}

                {eintrag.description && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {eintrag.description}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 1
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {formatRestzeit(
                      eintrag.created_at,
                      BEARBEITUNGSFRIST_STUNDEN,
                      unbegrenztBearbeitbar
                    )}
                  </Typography>
                  <Box>{aktionen(eintrag)}</Box>
                </Box>
              </CardContent>
            </Card>
          ))}

        <Paper sx={{ mt: 1 }}>{paginierung}</Paper>
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // Tabellenansicht
  // -------------------------------------------------------------------------
  const spaltenAnzahl = 7 + (zeigeMitarbeiter ? 1 : 0) + (zeigeErstelltVon ? 1 : 0) + 1;

  const sortierKopf = (feld: SortierFeld, label: string) => (
    <TableSortLabel
      active={sortierung.feld === feld}
      direction={sortierung.feld === feld ? sortierung.richtung : 'desc'}
      onClick={() => onSortieren(feld)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Paper>
      <TableContainer sx={{ position: 'relative' }}>
        {/*
          Beim Nachladen bleibt die Tabelle stehen und wird nur abgedunkelt.
          Vorher verschwand sie komplett, wodurch das Layout sprang.
        */}
        {laedt && eintraege.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 6,
              zIndex: 2
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Table size="small" sx={{ opacity: laedt && eintraege.length > 0 ? 0.6 : 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>{sortierKopf('date', 'Datum')}</TableCell>
              <TableCell>Projekt</TableCell>
              {zeigeMitarbeiter && <TableCell>Mitarbeiter</TableCell>}
              <TableCell>Start</TableCell>
              <TableCell>Ende</TableCell>
              <TableCell>Pause</TableCell>
              <TableCell align="right">{sortierKopf('hours', 'Stunden')}</TableCell>
              <TableCell>Beschreibung</TableCell>
              {zeigeErstelltVon && <TableCell>Erstellt von</TableCell>}
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {laedt && eintraege.length === 0 &&
              [0, 1, 2, 3, 4].map(i => (
                <TableRow key={i}>
                  <TableCell colSpan={spaltenAnzahl}>
                    <Skeleton height={28} />
                  </TableCell>
                </TableRow>
              ))}

            {leer && (
              <TableRow>
                <TableCell colSpan={spaltenAnzahl} sx={{ border: 0 }}>
                  {leerAnzeige}
                </TableCell>
              </TableRow>
            )}

            {eintraege.map(eintrag => (
              <TableRow key={eintrag.id} hover>
                <TableCell>{formatDatum(eintrag.date)}</TableCell>
                <TableCell>{eintrag.projectName}</TableCell>
                {zeigeMitarbeiter && <TableCell>{eintrag.userName}</TableCell>}
                <TableCell>{eintrag.startTime || '–'}</TableCell>
                <TableCell>{eintrag.endTime || '–'}</TableCell>
                <TableCell>{formatPause(eintrag.breakDuration)}</TableCell>
                <TableCell align="right">{formatStunden(eintrag.hours)}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 280,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Tooltip title={eintrag.description || ''}>
                    <span>{eintrag.description || '–'}</span>
                  </Tooltip>
                </TableCell>
                {zeigeErstelltVon && <TableCell>{eintrag.createdByUserName}</TableCell>}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {aktionen(eintrag)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {paginierung}
    </Paper>
  );
};

export default ZeiteintragTabelle;
